import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { ToggleField } from '@/features/academic/components/ToggleField'
import { useExamTransition } from '../api'
import { examStatusLabel, transitionCopy } from '../status'
import type { Exam, ExamTransition } from '../types'

/** Buttons for the lifecycle transitions the exam currently allows, each behind a confirmation. */
export function TransitionActions({ exam }: { exam: Exam }) {
  const { t } = useTranslation('exams')
  const [pending, setPending] = useState<ExamTransition | null>(null)
  return (
    <>
      {exam.allowedTransitions.map((tn) => (
        <Button
          key={tn}
          variant={tn === 'publish' ? 'primary' : tn === 'reopen' || tn === 'lock' ? 'secondary' : 'soft'}
          onClick={() => setPending(tn)}
        >
          {t(transitionCopy[tn].label)}
        </Button>
      ))}
      {pending ? (
        <TransitionDialog exam={exam} transition={pending} onClose={() => setPending(null)} />
      ) : null}
    </>
  )
}

function TransitionDialog({
  exam,
  transition,
  onClose,
}: {
  exam: Exam
  transition: ExamTransition
  onClose: () => void
}) {
  const { t } = useTranslation('exams')
  const mutation = useExamTransition()
  const [allowIncomplete, setAllowIncomplete] = useState(false)
  const copy = transitionCopy[transition]
  const missing = Math.max(0, exam.marksExpected - exam.marksEntered)

  const run = () =>
    mutation.mutate(
      { id: exam.id, transition, allowIncomplete },
      {
        onSuccess: (e) => {
          toast.success(t('{{name}}: {{status}}', { name: exam.name, status: t(examStatusLabel[e.status]) }))
          onClose()
        },
        onError: (err) => toast.error(t('Not possible right now'), toApiError(err).message),
      },
    )

  return (
    <Dialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="sm"
      dismissible={!mutation.isPending}
      icon={copy.tone === 'danger' ? <AlertTriangle className="text-danger-600" /> : undefined}
      title={t(copy.title)}
      description={t(copy.description)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('Cancel')}
          </Button>
          <Button
            variant={copy.tone === 'danger' ? 'danger' : 'primary'}
            onClick={run}
            loading={mutation.isPending}
            disabled={mutation.isPending || (transition === 'publish' && missing > 0 && !allowIncomplete)}
            autoFocus
          >
            {t(copy.label)}
          </Button>
        </>
      }
    >
      {transition === 'publish' && missing > 0 ? (
        <div className="space-y-3">
          <Alert tone="warning" title={t('{{count}} marks not entered', { count: missing })}>
            {t('Missing marks count as 0 and a failed paper.')}
          </Alert>
          <ToggleField
            label={t('Publish anyway')}
            checked={allowIncomplete}
            onCheckedChange={setAllowIncomplete}
          />
        </div>
      ) : null}
    </Dialog>
  )
}
